<script lang="ts">
  // Das Lama: Symbol der Suche UND ihr Zustandsanzeiger. Es kam mit
  // Ollama; seit 21.10.2026 rechnet bge-m3 im Inferenzdienst (llama.cpp,
  // docs/inferenz.md) -- das Lama blieb.
  //
  // Es sagt in einem Blick, ob gerade semantisch gesucht wird oder ob
  // die Suche still auf Fuzzy zurueckgefallen ist -- ein Rueckfall, den
  // man sonst nur an schlechteren Treffern merkt, ohne zu wissen warum.
  //
  // Vier Zustaende:
  //   init  -- die Probe laeuft noch (sehr kurz, pulsiert)
  //   idle  -- bereit, gerade nichts zu tun
  //   busy  -- ein Embedding-Call laeuft (Ping-Halo)
  //   off   -- Inferenzdienst nicht erreichbar, nur Fuzzy (ausgegraut)
  //
  // Lag urspruenglich in der TopBar-Pille. Beim Rahmen-Umbau am 18.08.
  // fiel die obere Leiste weg und das Lama mit ihr -- auf dem
  // Schreibtisch gab es danach keinen Hinweis mehr auf den Zustand der
  // KI-Suche (20.08.). Es lebt jetzt hier als eigene Komponente, damit
  // Palette (Schreibtisch) und TopBar (Telefon) DASSELBE Lama zeigen
  // und nicht zwei Kopien auseinanderlaufen.

  import { t } from "./i18n.svelte";
  import {
    isAvailable as aiAvailable,
    isBusy as aiBusy,
    isReady as aiReady,
    model as aiModel,
  } from "./semantic-search.svelte";

  interface Props {
    /** Kantenlaenge des Lamas in px. */
    size?: number;
    /**
     * Optional: macht das Lama klickbar und fuehrt zur Inferenz-Seite.
     * Ohne diesen Prop bleibt es ein reiner Anzeiger (so in der TopBar,
     * wo ein Klick den Fokus aus dem Suchfeld reissen wuerde).
     */
    onopen?: () => void;
  }
  let { size = 14, onopen }: Props = $props();

  const zustand = $derived.by(() => {
    if (!aiReady()) return "init";
    if (!aiAvailable()) return "off";
    if (aiBusy()) return "busy";
    return "idle";
  });

  const titel = $derived.by(() => {
    const m = aiModel() ?? "";
    if (zustand === "init") return t("ai.status.init", "KI-Suche startet …");
    if (zustand === "off")
      return t("ai.status.off", "KI-Suche offline – Fuzzy-Fallback aktiv");
    if (zustand === "busy") return t("ai.status.busy", `KI-Suche rechnet (${m})`);
    return t("ai.status.idle", `KI-Suche bereit (${m})`);
  });

  const farbe = $derived(
    zustand === "busy"
      ? "text-laeuft"
      : zustand === "idle"
        ? "text-fg"
        : zustand === "off"
          ? "text-fg-subtle/40"
          : "text-fg-subtle animate-pulse",
  );
</script>

<!-- role explizit: svelte:element kann die Rolle nicht statisch
     ableiten. Klickbar = Schalter, sonst eine Grafik mit Beschriftung
     (der Tooltip ist zugleich das aria-label). -->
<svelte:element
  this={onopen ? "button" : "span"}
  type={onopen ? "button" : undefined}
  onclick={onopen}
  role={onopen ? "button" : "img"}
  title={titel}
  aria-label={titel}
  class="relative inline-flex shrink-0 items-center justify-center"
  class:cursor-pointer={!!onopen}
  style="width: {size + 2}px; height: {size + 2}px;"
>
  {#if zustand === "busy"}
    <span
      class="bg-laeuft/30 absolute inset-0 animate-ping rounded-full"
      aria-hidden="true"
    ></span>
  {/if}
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="round"
    stroke-linejoin="round"
    width={size}
    height={size}
    class="relative {farbe}"
    aria-hidden="true"
  >
    <!-- Lama: zwei pointy Ohren, runde Kopf/Koerper-Silhouette,
         zwei Augen-Punkte, Schnauze als Oval mit Nase. -->
    <path d="M6.5 8 C5.5 5.5 5.5 3 6.5 1.5 C7.8 2.5 8 5 8 7" />
    <path d="M17.5 8 C18.5 5.5 18.5 3 17.5 1.5 C16.2 2.5 16 5 16 7" />
    <path
      d="M4.5 13 C4.5 9 7.5 6 12 6 C16.5 6 19.5 9 19.5 13 V17 C19.5 19.2 17.7 21 15.5 21 H8.5 C6.3 21 4.5 19.2 4.5 17 Z"
    />
    <circle cx="9.3" cy="12.5" r="0.85" fill="currentColor" stroke="none" />
    <circle cx="14.7" cy="12.5" r="0.85" fill="currentColor" stroke="none" />
    <path
      d="M9 17 C9 15.6 10.3 14.5 12 14.5 C13.7 14.5 15 15.6 15 17 C15 18.4 13.7 19.5 12 19.5 C10.3 19.5 9 18.4 9 17 Z"
    />
    <circle cx="12" cy="17.2" r="0.45" fill="currentColor" stroke="none" />
  </svg>
</svelte:element>
